import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type ConnectionStatus = "connected" | "disconnected" | "reconnecting";

export interface SubscriptionState {
  // Connection status
  connectionStatus: ConnectionStatus;
  lastConnectedAt: string | null;
  lastDisconnectedAt: string | null;

  // Reconnection tracking
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  nextReconnectAt: string | null;

  // Error state
  lastError: string | null;
  errorDismissed: boolean;
}

const initialState: SubscriptionState = {
  connectionStatus: "disconnected",
  lastConnectedAt: null,
  lastDisconnectedAt: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  nextReconnectAt: null,
  lastError: null,
  errorDismissed: false,
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setConnected: (state) => {
      state.connectionStatus = "connected";
      state.lastConnectedAt = new Date().toISOString();
      state.reconnectAttempts = 0;
      state.nextReconnectAt = null;
      state.lastError = null;
      state.errorDismissed = false;
    },
    setDisconnected: (state, action: PayloadAction<string | null>) => {
      state.connectionStatus = "disconnected";
      state.lastDisconnectedAt = new Date().toISOString();
      state.lastError = action.payload;
      state.errorDismissed = false;
    },
    setReconnecting: (
      state,
      action: PayloadAction<{ attempt: number; nextRetryMs: number }>
    ) => {
      state.connectionStatus = "reconnecting";
      state.reconnectAttempts = action.payload.attempt;
      state.nextReconnectAt = new Date(
        Date.now() + action.payload.nextRetryMs
      ).toISOString();
    },
    dismissError: (state) => {
      state.errorDismissed = true;
    },
    resetReconnection: (state) => {
      state.reconnectAttempts = 0;
      state.nextReconnectAt = null;
    },
  },
});

export const {
  setConnected,
  setDisconnected,
  setReconnecting,
  dismissError,
  resetReconnection,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
